import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import { LoggerService } from '@marmon/frontend/logging';
import {
	Insight,
	InsightRunProgress,
	insightNarrativeText,
	InsightResponseMeta,
	insightResponseMeta,
	isCompleteInsight,
	isPollableInsight,
} from '@marmon/shared/types/krista';
import { TuiButton } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import {
	AnalysisStateService,
	AnalysisSummaryInsightService,
} from '../../services';
import { InsightPanelComponent } from '../insight-panel/insight-panel.component';
import { InsightProgressComponent } from '../insight-progress/insight-progress.component';

export type AnalysisSummaryPhase =
	| 'loading'
	| 'generate'
	| 'generating'
	| 'view';

/**
 * Header action for the whole-analysis summary. The button stays in a
 * loading skeleton until the insight cache answers, then offers generate
 * or view. Generating shows the shared progress block. Viewing opens
 * the shared insight panel.
 */
@Component({
	standalone: true,
	selector: 'mar-analysis-summary',
	imports: [
		TranslocoPipe,
		TuiButton,
		TuiSkeleton,
		InsightPanelComponent,
		InsightProgressComponent,
	],
	templateUrl: 'analysis-summary.component.html',
	styleUrl: 'analysis-summary.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'flex-shrink-0',
		'[attr.data-phase]': 'phase()',
	},
})
export class AnalysisSummaryComponent implements OnInit {
	private readonly transloco = inject(TranslocoService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly state = inject(AnalysisStateService);
	private readonly insights = inject(AnalysisSummaryInsightService);
	private readonly logger = inject(LoggerService);
	private readonly alerts = inject(AlertService);
	private request: Subscription | null = null;
	private trackedKey = '';

	private readonly analysis = toSignal(this.state.analysis$, {
		initialValue: this.state.analysis,
	});

	private readonly calculation = toSignal(this.state.calculation$, {
		initialValue: this.state.calculation,
	});

	readonly phase = signal<AnalysisSummaryPhase>('loading');
	readonly narrative = signal('');
	readonly progress = signal<InsightRunProgress | null>(null);
	readonly meta = signal<InsightResponseMeta | null>(null);
	readonly panelOpen = signal(false);

	readonly canGenerate = computed(
		() => !!this.analysis()?.id && !!this.calculation()?.id,
	);

	readonly progressPercent = computed(() =>
		Math.round((this.progress()?.value ?? 0) * 100),
	);

	readonly progressMessage = computed(
		() => this.progress()?.message?.trim() ?? '',
	);

	constructor() {
		this.destroyRef.onDestroy(() => this.request?.unsubscribe());
	}

	ngOnInit() {
		combineLatest([this.state.analysis$, this.state.calculation$])
			.pipe(
				map(
					([analysis, calculation]) =>
						`${analysis?.id ?? ''}:${calculation?.id ?? ''}`,
				),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((key) => {
				if (key === this.trackedKey) return;
				this.trackedKey = key;
				this.restore();
			});
	}

	onGenerate() {
		if (this.phase() === 'generating' || this.phase() === 'loading') {
			return;
		}

		const analysisId = this.analysis()?.id;
		const calculationId = this.calculation()?.id;
		if (!analysisId || !calculationId) return;

		this.request?.unsubscribe();
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);
		this.phase.set('generating');
		this.request = this.insights
			.generate(analysisId, calculationId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (insight) => this.onInsight(insight),
				error: (error: unknown) => this.onRequestError(error),
				complete: () => {
					if (this.phase() === 'generating') this.showFailure();
				},
			});
	}

	onView() {
		if (!this.narrative()) return;
		this.panelOpen.set(true);
	}

	onClosePanel() {
		this.panelOpen.set(false);
	}

	private onInsight(insight: Insight, notify = true) {
		if (isPollableInsight(insight)) {
			this.progress.set(insight.progress ?? null);
			this.phase.set('generating');
			return;
		}

		if (insight.status === 'failed') {
			this.showFailure(insight.error?.message ?? null, notify);
			return;
		}

		const text = isCompleteInsight(insight)
			? insightNarrativeText(insight.response)
			: '';
		if (!text) {
			this.showFailure(null, notify);
			return;
		}

		this.narrative.set(text);
		this.meta.set(insightResponseMeta(insight.response));
		this.phase.set('view');
	}

	private onRequestError(error: unknown) {
		this.logger.captureException(
			error instanceof Error
				? error
				: new Error('Analysis summary request failed'),
			{ source: 'AnalysisSummaryComponent.generate' },
		);
		this.alerts.open('generic-error').subscribe();
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.phase.set('generate');
	}

	private showFailure(message: string | null = null, notify = true) {
		if (notify) {
			const text =
				message?.trim() ||
				this.transloco.translate('analysis.summary.error') ||
				'';
			this.logger.captureException(new Error(text), {
				source: 'AnalysisSummaryComponent.generate',
			});
			this.alerts.open('generic-error').subscribe();
		}
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);
		this.phase.set('generate');
	}

	private restore() {
		this.request?.unsubscribe();
		this.request = null;
		this.narrative.set('');
		this.meta.set(null);
		this.progress.set(null);
		this.panelOpen.set(false);

		const analysisId = this.analysis()?.id;
		const calculationId = this.calculation()?.id;
		if (!analysisId || !calculationId) {
			this.phase.set('generate');
			return;
		}

		this.phase.set('loading');
		this.request = this.insights
			.load(analysisId, calculationId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (insight) => {
					if (!insight) {
						this.phase.set('generate');
						return;
					}
					this.onInsight(insight, false);
				},
				error: (error: unknown) => this.onRequestError(error),
			});
	}
}
